import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  Radio,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  useGetBillingDetailsQuery,
  useCancelSubscriptionMutation,
  useResumeSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useGetSubscriptionsListQuery,
  SubscriptionPlan,
} from "../../api/subscriptionApi";
import { Column, DataTable } from "../../components/common/DataTable/DataTable";
import PageTitle from "../../components/common/PageTitle";
import { PaymentIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import UpdatePaymentMethodDialog from "./UpdatePaymentMethodDialog";
import PlanSectionsList from "../../components/subscription/PlanSectionsList";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

const SubscriptionDashboardContent: React.FC = () => {
  const { data: billingDetails, isLoading, error } = useGetBillingDetailsQuery(undefined, {
    // Refetch subscription data every time component mounts
    refetchOnMountOrArgChange: true,
    // Refetch when window regains focus
    refetchOnFocus: true,
  });
  const { data: subscriptionPlans } = useGetSubscriptionPlansQuery();
  // Get subscription list to access synced vehicle count (for Admin, returns only their tenant's subscription)
  const { data: subscriptionsList } = useGetSubscriptionsListQuery();
  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [resumeSubscription, { isLoading: isResuming }] = useResumeSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] = useReactivateSubscriptionMutation();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [vehicleCount, setVehicleCount] = useState<number>(1);
  const [updatePaymentDialogOpen, setUpdatePaymentDialogOpen] = useState(false);

  const selectedPlan: SubscriptionPlan | undefined = useMemo(
    () => subscriptionPlans?.find((p) => p.id === selectedPlanId),
    [subscriptionPlans, selectedPlanId]
  );

  const handleCancelSubscription = async (immediately: boolean) => {
    try {
      await cancelSubscription(immediately).unwrap();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      await resumeSubscription().unwrap();
    } catch (error) {
      console.error("Failed to resume subscription:", error);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!selectedPlanId || !vehicleCount || vehicleCount <= 0) return;

    try {
      await reactivateSubscription({
        subscriptionPlanId: selectedPlanId,
        vehicleCount: vehicleCount
      }).unwrap();
      setReactivateDialogOpen(false);
      setSelectedPlanId(null);
      setVehicleCount(1);
    } catch (error) {
      console.error("Failed to reactivate subscription:", error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "trialing":
        return "info";
      case "past_due":
        return "warning";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const paymentHistoryColumns: Column[] = [
    {
      field: "paidAt",
      headerName: "Date",
      renderCell: (_value, payment) =>
        formatDate(payment.paidAt || payment.createdAt),
    },
    {
      field: "description",
      headerName: "Description",
      renderCell: (value) => value || "Subscription payment",
    },
    {
      field: "amount",
      headerName: "Amount",
      renderCell: (value, payment) => formatCurrency(value, payment.currency),
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: (value) => (
        <Chip
          label={value.toUpperCase()}
          color={getPaymentStatusColor(value) as any}
          size="small"
          icon={
            value === "succeeded" ? (
              <CheckCircleIcon />
            ) : value === "failed" ? (
              <CancelIcon />
            ) : undefined
          }
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert severity="error">
          Failed to load subscription details. Please try again later.
        </Alert>
      </PageLayout>
    );
  }

  if (!billingDetails) {
    return (
      <PageLayout>
        <Alert severity="info">No billing information available.</Alert>
      </PageLayout>
    );
  }

  const { subscription, subscriptionPlan, paymentMethod, recentPayments, cancelAtPeriodEnd, canReactivate } = billingDetails;
  
  // Get synced vehicle count from subscriptions list (for Admin, this will be their tenant's subscription)
  const currentSubscriptionList = subscriptionsList?.find(s => s.subscriptionId === subscription?.id);
  const syncedVehicleCount = currentSubscriptionList?.syncedVehicleCount ?? 0;

  return (
    <PageLayout>
      <PageTitle title="Subscription Dashboard" icon={<PaymentIcon />} />

      {/* Subscription Overview Card */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Current Subscription"
              avatar={<AttachMoneyIcon color="primary" />}
            />
            <CardContent>
              {subscription && subscriptionPlan && subscription.status !== "canceled" ? (
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{subscriptionPlan.name}</Typography>
                    <Chip
                      label={subscription.status.toUpperCase()}
                      color={getStatusColor(subscription.status) as any}
                      size="small"
                    />
                  </Box>

                  {subscriptionPlan.description && (
                    <Typography variant="body2" color="text.secondary">
                      {subscriptionPlan.description}
                    </Typography>
                  )}

                  <Box>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(subscriptionPlan.pricePerVehicle, subscriptionPlan.currency)}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {" "}per vehicle/month
                      </Typography>
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(subscription.startDate)}
                      </Typography>
                    </Grid>

                    {subscription.status === "trialing" && subscription.trialEndDate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Trial Ends
                        </Typography>
                        <Typography variant="body1" color="info.main">
                          {formatDate(subscription.trialEndDate)}
                        </Typography>
                      </Grid>
                    )}

                    {subscription.status === "active" && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Next Billing Date
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(subscription.currentPeriodEnd)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Vehicle Quantity Information */}
                  {subscription.vehicleQuantity !== undefined && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Vehicle Quantity
                          </Typography>
                          <Typography variant="h6" color="primary">
                            {subscription.vehicleQuantity}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Maximum vehicles allowed
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Synced Vehicles
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: syncedVehicleCount >= (subscription.vehicleQuantity || 0) 
                                  ? "success.main" 
                                  : "warning.main",
                                fontWeight: syncedVehicleCount >= (subscription.vehicleQuantity || 0) ? 600 : 400
                              }}
                            >
                              {syncedVehicleCount}
                            </Typography>
                            {syncedVehicleCount >= (subscription.vehicleQuantity || 0) && (
                              <CheckCircleIcon sx={{ fontSize: 20, color: "success.main" }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Currently synced from Samsara
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {cancelAtPeriodEnd && (
                    <Alert severity="warning" icon={<WarningIcon />}>
                      Your subscription will be canceled on{" "}
                      {formatDate(subscription.currentPeriodEnd)}
                    </Alert>
                  )}

                  <Box display="flex" gap={2} mt={2}>
                    {cancelAtPeriodEnd ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<RefreshIcon />}
                        onClick={handleResumeSubscription}
                        disabled={isResuming}
                      >
                        {isResuming ? "Resuming..." : "Resume Subscription"}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </Box>
                </Stack>
              ) : (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {subscription?.status === "canceled"
                      ? "Your subscription has been canceled."
                      : "You don't have an active subscription."}
                  </Alert>

                  {(canReactivate || subscription?.status === "canceled") && subscriptionPlans && subscriptionPlans.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setSelectedPlanId(subscriptionPlans[0].id);
                        setVehicleCount(1);
                        setReactivateDialogOpen(true);
                      }}
                    >
                      Reactivate Subscription
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Method Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Payment Method"
              avatar={<CreditCardIcon color="primary" />}
            />
            <CardContent>
              {paymentMethod ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Card
                    </Typography>
                    <Typography variant="h6">
                      {paymentMethod.cardBrand?.toUpperCase()} •••• {paymentMethod.cardLast4}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Expires
                    </Typography>
                    <Typography variant="body1">
                      {paymentMethod.cardExpiryMonth}/{paymentMethod.cardExpiryYear}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setUpdatePaymentDialogOpen(true)}
                  >
                    Update Payment Method
                  </Button>
                </Stack>
              ) : (
                <Alert severity="warning">
                  No payment method on file. Please add a payment method.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment History */}
      <Card>
        <CardHeader title="Payment History" />
        <CardContent>
          {recentPayments && recentPayments.length > 0 ? (
            <DataTable
              items={recentPayments}
              columns={paymentHistoryColumns}
              totalCount={recentPayments.length}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No payment history available.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? You can choose to cancel at the
            end of your current billing period or immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Subscription</Button>
          <Button
            onClick={() => handleCancelSubscription(false)}
            color="warning"
            disabled={isCanceling}
          >
            Cancel at Period End
          </Button>
          <Button
            onClick={() => handleCancelSubscription(true)}
            color="error"
            disabled={isCanceling}
          >
            Cancel Immediately
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate Subscription Dialog */}
      <Dialog
        open={reactivateDialogOpen}
        onClose={() => {
          setReactivateDialogOpen(false);
          setSelectedPlanId(null);
          setVehicleCount(1);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reactivate Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Choose a subscription plan and number of vehicles to reactivate your subscription.
          </DialogContentText>

          {/* Plan Selection - Similar to Signup Flow */}
          {subscriptionPlans && subscriptionPlans.length > 0 && (
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {subscriptionPlans.map((plan) => (
                <Grid item xs={12} md={6} key={plan.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      background: selectedPlanId === plan.id
                        ? "rgba(220, 38, 38, 0.08)"
                        : "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      border: selectedPlanId === plan.id
                        ? "2px solid #dc2626"
                        : "1px solid rgba(100, 116, 139, 0.2)",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: selectedPlanId === plan.id ? "#dc2626" : "rgba(220, 38, 38, 0.5)",
                        background: selectedPlanId === plan.id
                          ? "rgba(220, 38, 38, 0.12)"
                          : "rgba(255, 255, 255, 0.9)",
                        boxShadow: "0 20px 40px rgba(220, 38, 38, 0.15)",
                      },
                    }}
                    onClick={() => setSelectedPlanId(plan.id)}
                  >
                    <CardContent sx={{ p: 3.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", letterSpacing: "-0.5px" }}>
                          {plan.name}
                        </Typography>
                        <Radio
                          checked={selectedPlanId === plan.id}
                          onChange={() => setSelectedPlanId(plan.id)}
                          sx={{
                            color: "rgba(30, 41, 59, 0.3)",
                            "&.Mui-checked": {
                              color: "#dc2626",
                            },
                          }}
                        />
                      </Box>

                      <Chip
                        label={plan.code}
                        size="small"
                        sx={{
                          mb: 3,
                          backgroundColor: "rgba(220, 38, 38, 0.15)",
                          color: "#dc2626",
                          fontWeight: 600,
                          fontSize: "12px",
                          height: "24px",
                        }}
                      />

                      <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5, letterSpacing: "-1px" }}>
                        {formatCurrency(plan.pricePerVehicle, plan.currency)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                        per vehicle / month
                      </Typography>

                      {plan.trialPeriodDays > 0 && (
                        <Box
                          sx={{
                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                            borderRadius: "8px",
                            p: 1.5,
                            mb: 2,
                            border: "1px solid rgba(220, 38, 38, 0.2)"
                          }}
                        >
                          <Typography sx={{ color: "#dc2626", fontWeight: 600, fontSize: "14px" }}>
                            ✓ {plan.trialPeriodDays}-day free trial
                          </Typography>
                        </Box>
                      )}

                      {plan.description && (
                        <Typography variant="body2" sx={{ color: "#64748b", mb: 2, lineHeight: 1.6 }}>
                          {plan.description}
                        </Typography>
                      )}

                      <PlanSectionsList planCode={plan.code} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Vehicle Count Input */}
          {selectedPlanId && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Number of vehicles"
                margin="normal"
                value={vehicleCount}
                onChange={(e) => setVehicleCount(Number(e.target.value) || 0)}
                inputProps={{ min: 1 }}
                required
                helperText={
                  selectedPlan
                    ? `Estimated monthly price: ${formatCurrency(
                      selectedPlan.pricePerVehicle * Math.max(vehicleCount || 0, 0),
                      selectedPlan.currency
                    )}`
                    : undefined
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(241, 245, 249, 0.8)",
                    borderRadius: "12px",
                    "& fieldset": {
                      borderColor: "rgba(100, 116, 139, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(100, 116, 139, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#dc2626",
                      borderWidth: "2px",
                    },
                    "& input": {
                      color: "#1e293b",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#64748b",
                    "&.Mui-focused": {
                      color: "#dc2626",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    color: "#dc2626",
                    fontWeight: 600,
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReactivateDialogOpen(false);
              setSelectedPlanId(null);
              setVehicleCount(1);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReactivateSubscription}
            color="primary"
            variant="contained"
            disabled={!selectedPlanId || !vehicleCount || vehicleCount <= 0 || isReactivating}
          >
            {isReactivating ? "Reactivating..." : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Payment Method Dialog */}
      <UpdatePaymentMethodDialog
        open={updatePaymentDialogOpen}
        onClose={() => setUpdatePaymentDialogOpen(false)}
      />
    </PageLayout>
  );
};

const SubscriptionDashboard: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <SubscriptionDashboardContent />
    </Elements>
  );
};

export default SubscriptionDashboard;

